// ARCHIVO GENERADO — NO EDITAR A MANO.
// Fuente: server/app/ui/blocks.py
// Regenerar con: make types


/** Acción que el usuario puede disparar desde el bloque.

`event` es el nombre que viaja de vuelta al agente en POST /actions. */
export interface BlockAction {
  "id": string;
  "kind": "submit" | "select" | "toggle" | "navigate";
  "label": string;
  "event": string;
}

/** Pistas de presentación que cualquier átomo comparte. */
export interface BlockMeta {
  "title"?: string | null;
  "subtitle"?: string | null;
  "width"?: "full" | "half" | "auto";
  "collapsible"?: boolean;
  "collapsed"?: boolean;
}

/** Destacado. `outside-syllabus` marca explícitamente contexto externo. */
export interface CalloutBlock {
  "id": string;
  "type": "callout";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "variant"?: "key-idea" | "warning" | "common-error" | "tip" | "note" | "outside-syllabus";
  "title"?: string | null;
  "body": string;
}

/** Gráfico de series. `waterfall` sirve para variaciones incrementales. */
export interface ChartBlock {
  "id": string;
  "type": "chart";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "chart"?: "line" | "bar" | "area" | "waterfall";
  "series": ChartSeries[];
  "x_labels": string[];
  "x_label"?: string | null;
  "y_label"?: string | null;
  "caption"?: string | null;
  "reference_lines"?: number[];
}

export interface ChartSeries {
  "name": string;
  "data": (number | null)[];
  "color_token"?: string | null;
}

/** Dos posturas enfrentadas: VAN vs TIR, costo de oportunidad vs de capital. */
export interface CompareBlock {
  "id": string;
  "type": "compare";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "left": CompareSide;
  "right": CompareSide;
  "verdict"?: string | null;
}

export interface CompareItem {
  "label": string;
  "detail"?: string | null;
}

export interface CompareSide {
  "title": string;
  "subtitle"?: string | null;
  "items": CompareItem[];
}

/** Tabla genérica con formato por columna. */
export interface DataTableBlock {
  "id": string;
  "type": "data_table";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "columns": TableColumn[];
  "rows": Record<string, unknown>[];
  "caption"?: string | null;
  "highlight_rows"?: number[];
}

/** Vista estructurada de un estado financiero del curso. */
export interface FinancialStatementBlock {
  "id": string;
  "type": "financial_statement";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "statement": "balance_sheet" | "income_statement" | "cash_flow";
  "periods": string[];
  "lines": StatementLine[];
  "currency"?: string | null;
  "caption"?: string | null;
}

export interface Flashcard {
  "front": string;
  "back": string;
  "hint"?: string | null;
}

/** Mazo con volteo, para glosario y repaso. */
export interface FlashcardsBlock {
  "id": string;
  "type": "flashcards";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "cards": Flashcard[];
}

/** Fórmula renderizada con KaTeX, leyenda de variables y derivación opcional. */
export interface FormulaBlock {
  "id": string;
  "type": "formula";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "latex": string;
  "caption"?: string | null;
  "variables"?: FormulaVariable[];
  "derivation"?: string[];
}

export interface FormulaVariable {
  "symbol": string;
  "meaning": string;
  "unit"?: string | null;
}

export interface MetricCard {
  "label": string;
  "value": string;
  "unit"?: string | null;
  "delta"?: string | null;
  "delta_direction"?: "up" | "down" | "flat" | null;
  "hint"?: string | null;
}

/** Tiles de indicadores: ROE, ROA, EBITDA, PER, margen neto. */
export interface MetricCardsBlock {
  "id": string;
  "type": "metric_cards";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "cards": MetricCard[];
}

/** Un preset empaquetado como bloque, para que viaje por el stream.

El agente pide un preset con `RenderPayload.preset`, pero lo que se emite y
se persiste es siempre un bloque: de otro modo un preset no sobreviviría a
la recarga de la conversación como sí hacen los átomos. Esta clase es esa
forma empaquetada — la misma que `block.add` transporta. */
export interface PresetBlock {
  "type": "preset";
  "id": string;
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "preset": PresetRef;
}

/** Preset de pantalla completa.

Al contrario que un átomo, el preset no trae sus bloques: el frontend los
resuelve a partir de `name` + `data`, lo que mantiene el payload pequeño y
permite que la UI controle la composición y el estilo. */
export interface PresetRef {
  "name": "course_overview" | "unit_dashboard" | "concept_deep_dive" | "case_study" | "assessment_brief" | "study_path";
  "data"?: Record<string, unknown>;
  "title"?: string | null;
}

/** Texto enriquecido: markdown con math inline ($...$) y citas. */
export interface ProseBlock {
  "id": string;
  "type": "prose";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "markdown": string;
}

/** Pregunta con revelar respuesta. Al responder emite `quiz.submit`. */
export interface QuizBlock {
  "id": string;
  "type": "quiz";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "question": string;
  "kind"?: "single_choice" | "multiple_choice" | "true_false";
  "options": QuizOption[];
  "correct": string[];
  "explanation"?: string | null;
  "hint"?: string | null;
  "module"?: string | null;
}

export interface QuizOption {
  "id": string;
  "text": string;
}

/** Pesos de evaluación: 20% CL + 45% EA + 35% TF. */
export interface RubricBlock {
  "id": string;
  "type": "rubric";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "total_label"?: string;
  "formula"?: string | null;
  "components": RubricComponent[];
}

export interface RubricComponent {
  "code": string;
  "label": string;
  "weight": number;
  "description"?: string | null;
  "modules"?: string[];
}

/** Calculadora interactiva. Al cambiar un input emite `simulator.change`.

El cálculo vive en el frontend (feedback instantáneo, sin round-trip) y el
backend solo declara el tipo y los valores iniciales. Los `kind` cubren los
cálculos del curso: VAN, TIR, WACC, capital de trabajo, interés compuesto. */
export interface SimulatorBlock {
  "id": string;
  "type": "simulator";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "kind": "npv" | "irr" | "wacc" | "working_capital" | "compound_interest";
  "inputs": SimulatorInput[];
  "cash_flows"?: number[];
  "hint"?: string | null;
  "module"?: string | null;
}

export interface SimulatorInput {
  "key": string;
  "label": string;
  "value": number;
  "unit"?: string | null;
  "min"?: number | null;
  "max"?: number | null;
  "step"?: number | null;
}

/** Fuentes citadas. Es la trazabilidad visible de la respuesta. */
export interface SourceListBlock {
  "id": string;
  "type": "source_list";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "sources": SourceRef[];
}

export interface SourceRef {
  "path": string;
  "title"?: string | null;
  "kind"?: "course" | "outside" | "pdf";
  "snippet"?: string | null;
  "trust"?: "unverified" | "machine-confirmed" | "human-reviewed" | null;
  "stale"?: boolean | null;
  "page"?: number | null;
}

export interface StatementLine {
  "label": string;
  "kind"?: "group" | "line" | "subtotal" | "total";
  "values": (string | null)[];
  "indent"?: number;
  "note"?: string | null;
}

export interface TableColumn {
  "key": string;
  "label": string;
  "align"?: "left" | "right" | "center";
  "format"?: "text" | "number" | "currency" | "percent" | "ratio";
}

/** Secuencia ordenada: cronograma de 8 semanas o pasos de un proceso. */
export interface TimelineBlock {
  "id": string;
  "type": "timeline";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "items": TimelineItem[];
  "orientation"?: "vertical" | "horizontal";
}

export interface TimelineItem {
  "label": string;
  "title": string;
  "description"?: string | null;
  "duration"?: string | null;
  "tags"?: string[];
  "status"?: "done" | "current" | "upcoming";
}

/** Cálculo resuelto paso a paso, con stepper. */
export interface WorkedExampleBlock {
  "id": string;
  "type": "worked_example";
  "schema_version"?: string;
  "meta"?: BlockMeta;
  "actions"?: BlockAction[];
  "source_refs"?: string[];
  "prompt": string;
  "steps": WorkedExampleStep[];
  "result_label": string;
  "result_value": string;
  "result_unit"?: string | null;
}

export interface WorkedExampleStep {
  "title": string;
  "body": string;
  "latex"?: string | null;
}

export const BLOCK_TYPES = ["callout", "chart", "compare", "data_table", "financial_statement", "flashcards", "formula", "metric_cards", "prose", "quiz", "rubric", "simulator", "source_list", "timeline", "worked_example"] as const;

export const PRESET_TYPES = ["course_overview", "unit_dashboard", "concept_deep_dive", "case_study", "assessment_brief", "study_path"] as const;

/** Argumento de la tool `render_ui`.

El agente emite bloques sueltos, un preset, o ambos. Se prefieren varios
bloques pequeños antes que uno gigante: algunos proxies truncan argumentos
largos de tool use.

`preset` es azúcar para el modelo: el backend lo reempaqueta como
`PresetBlock` antes de emitirlo, así que existe una sola forma en el
stream. */
export interface RenderPayload {
  "blocks"?: (ProseBlock | CalloutBlock | FormulaBlock | MetricCardsBlock | DataTableBlock | FinancialStatementBlock | ChartBlock | TimelineBlock | CompareBlock | QuizBlock | WorkedExampleBlock | SimulatorBlock | FlashcardsBlock | RubricBlock | SourceListBlock | PresetBlock)[];
  "preset"?: PresetRef | null;
  "narration"?: string | null;
}

export type Block = ProseBlock | CalloutBlock | FormulaBlock | MetricCardsBlock | DataTableBlock | FinancialStatementBlock | ChartBlock | TimelineBlock | CompareBlock | QuizBlock | WorkedExampleBlock | SimulatorBlock | FlashcardsBlock | RubricBlock | SourceListBlock | PresetBlock;
